import React, { useEffect, useState } from "react";
import { Box, Drawer, Image, Text, UnstyledButton } from "@mantine/core";
import { useNotifications } from "../../context/NotificationContext";
import { AlertsSheet } from "../notifications/AlertsSheet";
import { DESIGN_ICONS } from "../../theme/designIcons";
import { MOBILE_Z } from "../calendar/CalendarView/mobile/tokens";
import { isNavLinkActive, type NavSectionKey } from "./navSections";
import { useNavModel, type NavAccount } from "./useNavModel";
import styles from "./MobileNavDrawer.module.css";

type IconComponent = React.ComponentType<{ size?: number; stroke?: number }>;

export interface MobileNavItem {
  key: string;
  label: string;
  /** Second line under the label (e.g. the current location). */
  sublabel?: string;
  icon: IconComponent;
  active?: boolean;
  disabled?: boolean;
  /** Trailing chevron (the row expands its sub-pages). */
  chevron?: boolean;
  /** Sub-pages shown under the row (chevron points down). */
  expanded?: boolean;
  subItems?: MobileNavSubItem[];
  badge?: number;
  onSelect: () => void;
}

export interface MobileNavSubItem {
  key: string;
  label: string;
  active?: boolean;
  /** Locked by the subscription tier. */
  disabled?: boolean;
  onSelect: () => void;
}

/** Who is signed in, shown at the bottom. */
export type MobileNavAccount = Pick<
  NavAccount,
  "name" | "initials" | "salonName"
>;

export interface MobileNavDrawerViewProps {
  opened: boolean;
  onClose: () => void;
  items: MobileNavItem[];
  footItems: MobileNavItem[];
  /** Signed-in user, shown at the bottom. */
  account?: MobileNavAccount | null;
}

const NavRow: React.FC<{ item: MobileNavItem }> = ({ item }) => {
  const Icon = item.icon;
  const Chevron = item.expanded
    ? DESIGN_ICONS.ChevronDown
    : DESIGN_ICONS.ChevronRight;
  return (
    <>
      <UnstyledButton
        className={styles.item}
        data-active={!!item.active}
        data-disabled={!!item.disabled}
        disabled={item.disabled}
        onClick={item.onSelect}
        aria-current={item.active && !item.subItems ? "page" : undefined}
        aria-expanded={item.subItems ? !!item.expanded : undefined}
      >
        <Box component="span" className={styles.icon}>
          <Icon size={19} stroke={1.8} />
          {!!item.badge && item.badge > 0 && (
            <Text span className={styles.badge}>
              {item.badge > 99 ? "99+" : item.badge}
            </Text>
          )}
        </Box>
        <Box component="span" className={styles.label}>
          <Text span inherit>
            {item.label}
          </Text>
          {item.sublabel && (
            <Text span className={styles.sublabel}>
              {item.sublabel}
            </Text>
          )}
        </Box>
        {item.chevron && (
          <Box component="span" className={styles.chev}>
            <Chevron size={17} stroke={1.8} />
          </Box>
        )}
      </UnstyledButton>
      {item.expanded &&
        item.subItems?.map((sub) => (
          <UnstyledButton
            key={sub.key}
            className={styles.subItem}
            data-active={!!sub.active}
            data-disabled={!!sub.disabled}
            disabled={sub.disabled}
            onClick={sub.onSelect}
            aria-current={sub.active ? "page" : undefined}
          >
            {sub.label}
          </UnstyledButton>
        ))}
    </>
  );
};

/** Left navigation drawer over a scrim (presentational). */
export const MobileNavDrawerView: React.FC<MobileNavDrawerViewProps> = ({
  opened,
  onClose,
  items,
  footItems,
  account,
}) => (
  <Drawer
    opened={opened}
    onClose={onClose}
    position="left"
    size={300}
    withCloseButton={false}
    zIndex={MOBILE_Z.sheet}
    transitionProps={{ duration: 0 }}
    overlayProps={{ color: "#141414", backgroundOpacity: 0.34, blur: 0 }}
    padding={0}
    classNames={{ content: styles.content, body: styles.body }}
    styles={{ content: { maxWidth: "82%" } }}
    aria-label="Navigation"
  >
    <Box className={styles.head}>
      <Image src="/images/pagoda-logo.png" alt="" className={styles.logo} />
      <Text span className={styles.wordmark}>
        Pagoda
      </Text>
    </Box>
    <Box className={styles.list}>
      {items.map((item) => (
        <NavRow key={item.key} item={item} />
      ))}
    </Box>
    <Box className={styles.foot}>
      {footItems.map((item) => (
        <NavRow key={item.key} item={item} />
      ))}
      {account && (
        <Box className={styles.account} data-testid="mobile-nav-account">
          <Box component="span" className={styles.avatar} aria-hidden="true">
            {account.initials}
          </Box>
          <Box className={styles.accountText}>
            <Text span className={styles.accountName}>
              Signed in as {account.name}
            </Text>
            {account.salonName && (
              <Text span className={styles.accountSalon}>
                {account.salonName}
              </Text>
            )}
          </Box>
        </Box>
      )}
    </Box>
  </Drawer>
);

export interface MobileNavDrawerProps {
  opened: boolean;
  onClose: () => void;
}

/**
 * The app's mobile navigation (every page; the calendar opens it from its own
 * header band). The same grouped sections as the desktop rail, as an
 * accordion with the current page's section open, then Alerts / Chat /
 * Onboarding / Alerts / Chat / Switch location / Settings / Log out, always
 * visible.
 */
export const MobileNavDrawer: React.FC<MobileNavDrawerProps> = ({
  opened,
  onClose,
}) => {
  const nav = useNavModel();
  const { unacknowledgedCount } = useNotifications();
  const { pathname } = nav;
  const activeKey = nav.activeSection?.key ?? null;
  // One section open at a time; opening the sheet shows the current page's.
  const [expandedKey, setExpandedKey] = useState<NavSectionKey | null>(
    activeKey,
  );
  // Alerts opens as its own bottom sheet once the nav sheet closes.
  const [alertsOpen, setAlertsOpen] = useState(false);
  useEffect(() => {
    if (opened) setExpandedKey(activeKey);
  }, [opened, activeKey]);

  const go = (path: string) => () => {
    onClose();
    nav.navigate(path);
  };

  const items: MobileNavItem[] = nav.sections.map((section) => ({
    key: section.key,
    label: section.label,
    icon: section.icon,
    active: section.key === activeKey,
    chevron: true,
    expanded: section.key === expandedKey,
    subItems: section.links.map((link) => ({
      key: link.key,
      label: link.label,
      active: isNavLinkActive(link, pathname),
      disabled: !!link.locked,
      onSelect: go(link.path),
    })),
    onSelect: () =>
      setExpandedKey((open) => (open === section.key ? null : section.key)),
  }));

  const footItems: MobileNavItem[] = [];
  // Owners only, until every onboarding card is done; above Alerts as on
  // the desktop rail.
  if (nav.canAccessOnboarding) {
    footItems.push({
      key: "onboarding",
      label: "Onboarding",
      icon: DESIGN_ICONS.Rocket,
      active: pathname === "/onboarding",
      onSelect: go("/onboarding"),
    });
  }
  footItems.push({
    key: "alerts",
    label: "Alerts",
    icon: DESIGN_ICONS.Bell,
    badge: unacknowledgedCount,
    onSelect: () => {
      onClose();
      setAlertsOpen(true);
    },
  });
  if (nav.showChat) {
    footItems.push({
      key: "chat",
      label: "Chat",
      icon: DESIGN_ICONS.MessageCircle,
      badge: nav.chatUnreadCount,
      active: pathname.startsWith("/admin/chat"),
      onSelect: go("/admin/chat"),
    });
  }
  // TODO(nav): Help (design handoff §12: Ask a question / Report a bug)
  // goes here once both flows exist.
  if (nav.showLocationSwitcher) {
    const current = nav.availableLocations.find(
      (loc) => loc.salon_id === nav.selectedSalonId,
    );
    footItems.push({
      key: "switch-location",
      label: "Switch location",
      sublabel: current?.name,
      icon: DESIGN_ICONS.MapPin,
      onSelect: () => {
        onClose();
        nav.openLocationPicker();
      },
    });
  }
  footItems.push(
    {
      key: "settings",
      label: "Settings",
      icon: DESIGN_ICONS.Settings,
      active: pathname.startsWith("/admin/settings"),
      onSelect: go("/admin/settings/studio"),
    },
    {
      key: "log-out",
      label: "Log out",
      icon: DESIGN_ICONS.LogOut,
      onSelect: () => {
        onClose();
        nav.handleSignOut();
      },
    },
  );

  return (
    <>
      <MobileNavDrawerView
        opened={opened}
        onClose={onClose}
        items={items}
        footItems={footItems}
        account={nav.account}
      />
      <AlertsSheet opened={alertsOpen} onClose={() => setAlertsOpen(false)} />
    </>
  );
};
